import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  REFUND_TYPES,
  REFUND_METHOD,
  getPaymentTypeTranslation,
} from '@/utility';
import { useGetRefunds } from '@/api/hooks/refunds/useGetRefunds';
import Button from '@/components/CustomButtons/Button';
import moment from 'moment';
import loader from '@/assets/img/loader.svg';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';
import PayeeCategorySelect from '@/components/PayeeCategorySelect';
import TransactionStatusChip from '@/components/TransactionStatusChip';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const CheckPayments = ({ role = 'admin', smartPayablesAccountId }) => {
  const { t } = useTranslation();
  const listInnerRef = useRef();
  const [selectedRefunds, setSelectedRefunds] = useState([]);
  const { facilities } = useGetFacilities({
    enabled: role === 'admin',
    allColumns: false,
  });
  const {
    payments,
    loading,
    startDate,
    setPartialState,
    endDate,
    payeeCategory,
    search,
    refetchRefunds,
    hasMore,
    offSet,
    sortBy,
    handleSearch,
  } = useGetRefunds({
    defaultValues: {
      smartpayablesId: smartPayablesAccountId,
    },
  });

  const handleSearchChange = (e) => {
    setPartialState({ search: e.target.value });
    handleSearch(e.target.value);
  };

  const handleScroll = useCallback(() => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      const scrollThreshold = 50;
      if (
        scrollTop + clientHeight >= scrollHeight - scrollThreshold &&
        hasMore &&
        !loading &&
        smartPayablesAccountId
      ) {
        setPartialState({ offSet: offSet + 25 });
      }
    }
  }, [hasMore, loading, smartPayablesAccountId]);

  const sortByColumn = (columnName) => {
    setPartialState({
      sortBy: {
        name: columnName,
        order: sortBy.name === columnName ? (sortBy.order === 1 ? -1 : 1) : 1,
      },
    });
  };

  const sortArrow = (columnName) => {
    return columnName === sortBy.name ? (sortBy.order === 1 ? '↓' : '↑') : '';
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const visibleRefundIds = payments
        .filter((payment) => payment.refund_method === REFUND_METHOD.CHECK)
        .map((payment) => payment.id);
      setSelectedRefunds(visibleRefundIds);
    } else {
      setSelectedRefunds([]);
    }
  };

  const handleSelectRefund = (id) => {
    setSelectedRefunds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((refundId) => refundId !== id)
        : [...prevSelected, id],
    );
  };

  const isRefundSelected = (id) => selectedRefunds.includes(id);

  const handleGeneratePositivePay = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/smartpayables/generate-positive-pay`,
        {
          refund_ids: selectedRefunds,
          smartpayables_id: parseInt(smartPayablesAccountId),
        },
      );

      const { data } = response.data;

      if (data && data.fileName && data.fileUrl) {
        const downloadUrl = `${API_BASE_URL}/getFile?filePath=${encodeURIComponent(
          data.fileUrl,
        )}`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', data.fileName);
        link.setAttribute('target', '_blank');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        alert(
          t('checkPayments.alerts.positivePaySuccess', {
            fileName: data.fileName,
            totalAmount: data.totalAmount.toFixed(2),
            refundCount: data.refundCount,
          }),
        );

        setSelectedRefunds([]);
        if (refetchRefunds) {
          refetchRefunds();
        }
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error generating positive pay file:', error);
      alert(t('checkPayments.alerts.positivePayError'));
    }
  };

  useEffect(() => {
    if (smartPayablesAccountId) {
      setPartialState({ smartpayablesId: smartPayablesAccountId });
    }
  }, [smartPayablesAccountId]);

  if (!smartPayablesAccountId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <h3>{t('checkPayments.noAccountSelected')}</h3>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <Card>
        <CardBody>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
              alignItems: 'center',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {t('checkPayments.filters.from')}
              </label>
              <DatePicker
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '150px',
                }}
                id='dt'
                autoComplete='off'
                selected={startDate}
                onChange={(date) => setPartialState({ startDate: date })}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {t('checkPayments.filters.to')}
              </label>
              <DatePicker
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '150px',
                }}
                id='dt'
                autoComplete='off'
                selected={endDate}
                onChange={(date) => setPartialState({ endDate: date })}
              />
            </div>

            {role === 'admin' && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  id='facility'
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px',
                    backgroundColor: 'white',
                  }}
                  onChange={(e) =>
                    setPartialState({ facility: e.target.value })
                  }>
                  <option value={''}>
                    {t('checkPayments.filters.facility')}
                  </option>
                  {facilities.map((obj) => (
                    <option value={obj.id} key={obj.id}>
                      {obj.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PayeeCategorySelect
                value={payeeCategory}
                onChange={(e) =>
                  setPartialState({ payeeCategory: e.target.value })
                }
                allowedRefundTypes={Object.values(REFUND_TYPES)}
                role={role}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginLeft: 'auto',
              }}>
              <div style={{ position: 'relative' }}>
                <input
                  type='text'
                  placeholder={t('checkPayments.filters.search')}
                  value={search}
                  onChange={handleSearchChange}
                  style={{
                    padding: '8px 40px 8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '200px',
                  }}
                />
                <SearchIcon
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    fontSize: '20px',
                  }}
                />
              </div>
            </div>
          </div>
          {selectedRefunds.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Button
                color='info'
                size='sm'
                onClick={() => handleGeneratePositivePay()}>
                <FileDownloadIcon
                  fontSize='small'
                  style={{ marginRight: '5px' }}
                />
                {t('checkPayments.generatePositivePay')} (
                {selectedRefunds.length})
              </Button>
            </div>
          )}

          {loading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}>
              <img src={loader} alt={t('checkPayments.loading')} />
            </div>
          )}

          <div
            style={{
              overflow: 'auto',
              height: '70vh',
              width: '100%',
              position: 'relative',
            }}
            ref={listInnerRef}
            onScroll={handleScroll}>
            <Table width='100%'>
              <TableHead>
                <TableRow style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      indeterminate={
                        selectedRefunds.length > 0 &&
                        selectedRefunds.length < payments.length
                      }
                      checked={
                        payments.length > 0 &&
                        selectedRefunds.length === payments.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('created_at')}>
                    <strong>
                      {t('checkPayments.table.createdDate')}{' '}
                      {sortArrow('created_at')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('updated_at')}>
                    <strong>
                      {t('checkPayments.table.updatedDate')}{' '}
                      {sortArrow('updated_at')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('id')}>
                    <strong>
                      {t('checkPayments.table.cpdId')} {sortArrow('id')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('last_name')}>
                    <strong>
                      {t('checkPayments.table.customerInfo')}{' '}
                      {sortArrow('last_name')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_amount')}>
                    <strong>
                      {t('checkPayments.table.amount')}{' '}
                      {sortArrow('refund_amount')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('check_number')}>
                    <strong>
                      {t('checkPayments.table.checkNumber')}{' '}
                      {sortArrow('check_number')}
                    </strong>
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_type')}>
                    <strong>
                      {t('checkPayments.table.paymentCategory')}{' '}
                      {sortArrow('refund_type')}
                    </strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('checkPayments.table.refundStatus')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>
                      {t('checkPayments.table.transactionStatus')}
                    </strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('checkPayments.table.externalId')}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              {payments && payments.length > 0 ? (
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={isRefundSelected(payment.id)}
                          onChange={() => handleSelectRefund(payment.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {moment(payment.created_at).format('MM/DD/YYYY')}
                      </TableCell>
                      <TableCell>
                        {payment.updated_at
                          ? moment(payment.updated_at).format('MM/DD/YYYY')
                          : t('checkPayments.table.notAvailable')}
                      </TableCell>
                      <TableCell>{payment.id}</TableCell>
                      <TableCell>
                        {payment.first_name} {payment.last_name}
                        {payment.email && (
                          <p style={{ fontSize: '12px', margin: '2px 0 0 0' }}>
                            ({payment.email})
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        ${parseFloat(payment.refund_amount).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {payment.check_number ||
                          t('checkPayments.table.notAvailable')}
                      </TableCell>
                      <TableCell>
                        {payment.custom_payment_type ||
                          getPaymentTypeTranslation(payment.refund_type)}
                      </TableCell>
                      <TableCell>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: '#2196f3',
                            color: 'white',
                            fontSize: '12px',
                          }}>
                          {payment.refund_status ||
                            t('checkPayments.table.notAvailable')}
                        </span>
                      </TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        <TransactionStatusChip
                          status={payment.transaction_status}
                        />
                      </TableCell>
                      <TableCell>
                        {payment.transaction_external_id ||
                          t('checkPayments.table.notAvailable')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center' }}>
                      {t('checkPayments.table.noPayments')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CheckPayments;
